import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { reportsAPI } from "../../services/api";
import {
  Box, Typography, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip,
  CircularProgress, Alert, LinearProgress, Button
} from "@mui/material";

export default function QuestionAnalysis() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await reportsAPI.getQuestionAnalysis();
      setQuestions(res.data);
    } catch { setError("Failed to load question analysis."); }
    finally { setLoading(false); }
  };

  const exportExcel = () => {
    const rows = questions.map(q => ({
      "Question": q.question_text,
      "Module": q.module_title,
      "Type": q.question_type,
      "Difficulty": q.difficulty,
      "Critical": q.is_critical ? "Yes" : "No",
      "Times Used": q.times_used,
      "Times Correct": q.times_correct,
      "Times Failed": q.times_failed,
      "Success Rate %": q.success_rate,
      "Fail Rate %": q.fail_rate
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Question Analysis");
    XLSX.writeFile(wb, "question_analysis.xlsx");
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1a237e">Question Analysis</Typography>
          <Typography variant="body2" color="text.secondary">
            Questions sorted by highest failure rate
          </Typography>
        </Box>
        <Button variant="outlined" color="success" onClick={exportExcel}
          disabled={questions.length === 0}>
          Export Excel
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
      ) : questions.length === 0 ? (
        <Card sx={{ borderRadius: 3, p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No question attempt data available yet.</Typography>
        </Card>
      ) : (
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                    <TableCell><b>Question</b></TableCell>
                    <TableCell><b>Module</b></TableCell>
                    <TableCell><b>Difficulty</b></TableCell>
                    <TableCell><b>Times Used</b></TableCell>
                    <TableCell><b>Failed</b></TableCell>
                    <TableCell><b>Fail Rate</b></TableCell>
                    <TableCell><b>Success Rate</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questions.map((q) => (
                    <TableRow key={q.question_id} hover>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" fontWeight={q.is_critical ? 700 : 400}>
                          {q.question_text.length > 80 ? q.question_text.substring(0, 80) + "..." : q.question_text}
                        </Typography>
                        {q.is_critical && <Chip label="Critical" size="small" color="error" sx={{ mt: 0.5 }} />}
                      </TableCell>
                      <TableCell><Typography variant="body2">{q.module_title}</Typography></TableCell>
                      <TableCell>
                        <Chip label={q.difficulty} size="small"
                          color={q.difficulty === "hard" ? "error" : q.difficulty === "medium" ? "warning" : "success"} />
                      </TableCell>
                      <TableCell>{q.times_used}</TableCell>
                      <TableCell sx={{ color: q.times_failed > 0 ? "error.main" : "inherit", fontWeight: 600 }}>
                        {q.times_failed}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LinearProgress variant="determinate" value={q.fail_rate}
                            sx={{ width: 60, height: 8, borderRadius: 4,
                              "& .MuiLinearProgress-bar": { bgcolor: q.fail_rate > 50 ? "#d32f2f" : q.fail_rate > 25 ? "#f57c00" : "#388e3c" }
                            }} />
                          <Typography variant="body2" fontWeight={600}
                            color={q.fail_rate > 50 ? "error.main" : q.fail_rate > 25 ? "warning.main" : "success.main"}>
                            {q.fail_rate}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {q.success_rate}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
